<template>
  <div v-if="isVisible" class="rfid-power-switch-container">
    <el-button size="small" @click="togglePower" class="power-toggle-button">
      <div class="power-option">
        <component :is="currentIcon" class="power-icon" :class="`power-${displayLevel}`" />
        <span class="power-label">{{ currentLabel }}</span>
      </div>
    </el-button>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { SendToBizBrowser } from '@/utils/bizBrowser/sendToBizBrowser'
import IconRfidPowerLow from '@/components/svg/IconRfidPowerLow.vue'
import IconRfidPowerMid from '@/components/svg/IconRfidPowerMid.vue'
import IconRfidPowerHigh from '@/components/svg/IconRfidPowerHigh.vue'
import rfidDevicesConfig from '@/views/bizBrowser/config/rfidDevices.json'

type PowerDisplay = 'low' | 'medium' | 'high'

interface PowerLevels {
  low: number
  medium: number
  high: number
}

interface DeviceConfig {
  vendor: string
  model: string
  address: string
  powerLevel?: PowerLevels
}

const DEFAULT_POWER_LEVELS = { low: 1, medium: 2, high: 3 } // If you want to use this outside of the component, move this to a separate file

@Options({
  name: 'RfidPowerSwitch',
  components: {
    IconRfidPowerLow,
    IconRfidPowerMid,
    IconRfidPowerHigh,
  },
})
export default class RfidPowerSwitch extends Vue {
  isVisible = true

  get currentPower(): number {
    return this.$store.getters.bizBrowserState.rfidPowerLevel
  }

  get selectedDeviceKey(): string | null {
    return this.$store.getters.bizBrowserState.selectedDeviceKey
  }

  get powerLevels(): PowerLevels {
    if (this.selectedDeviceKey) {
      const deviceConfig = rfidDevicesConfig[this.selectedDeviceKey as keyof typeof rfidDevicesConfig] as DeviceConfig
      return deviceConfig?.powerLevel || DEFAULT_POWER_LEVELS
    }
    return DEFAULT_POWER_LEVELS
  }

  @Watch('selectedDeviceKey', { immediate: true })
  onSelectedDeviceKeyChanged(newValue: string | null) {
    this.isVisible = !!newValue
  }

  get displayLevel(): PowerDisplay {
    const { low, medium } = this.powerLevels
    if (this.currentPower <= low) {
      return 'low'
    }
    if (this.currentPower <= medium) {
      return 'medium'
    }
    return 'high'
  }

  get currentIcon() {
    const iconMap = {
      low: IconRfidPowerLow,
      medium: IconRfidPowerMid,
      high: IconRfidPowerHigh,
    }
    return iconMap[this.displayLevel]
  }

  get currentLabel() {
    const labelMap = {
      low: this.$t('Low'),
      medium: this.$t('Mid'),
      high: this.$t('High'),
    }
    return labelMap[this.displayLevel]
  }

  @Watch('currentPower')
  onPowerChanged() {
    SendToBizBrowser.idleMode()
    SendToBizBrowser.startScan()
  }

  togglePower() {
    const { low, medium, high } = this.powerLevels
    let nextPower: number

    if (this.currentPower <= low) {
      nextPower = medium
    } else if (this.currentPower <= medium) {
      nextPower = high
    } else {
      nextPower = low
    }
    SendToBizBrowser.updateRfidPowerLevel(nextPower)
  }
}
</script>

<style lang="scss" scoped>
.rfid-power-switch-container {
  display: flex;
  align-items: center;

  .power-toggle-button {
    width: 90px;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
}

.power-option {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: center;

  .power-icon {
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.3s ease;

    &.power-low {
      color: #67c23a;
    }

    &.power-medium {
      color: #e6a23c;
    }

    &.power-high {
      color: #f56c6c;
    }
  }

  .power-label {
    font-size: 0.85rem;
    font-weight: 500;
    width: 38px;
    text-align: left;
  }
}
</style>
